import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Download, Shield, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SecurityPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/export-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `snow-exchange-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/auth/request-deletion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deletion request confirmed. You will receive a confirmation email.');
      setShowDeleteModal(false);
      
      // Refresh user data to show cancellation option
      window.location.reload();
    } catch (error) {
      console.error('Deletion request error:', error);
      toast.error(error.response?.data?.detail || 'Failed to request deletion');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDeletion = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/cancel-deletion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deletion cancelled. Your account is safe!');
      window.location.reload();
    } catch (error) {
      console.error('Cancel deletion error:', error);
      toast.error('Failed to cancel deletion');
    }
  };

  const isDeletionScheduled = user?.deletion_scheduled_at;

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ color: '#1B3240' }}>
          Security & Privacy
        </h1>
        <p className="text-lg text-gray-600">
          Manage your account security and data privacy settings
        </p>
      </div>

      <div className="space-y-6">
        {/* Deletion Warning Banner */}
        {isDeletionScheduled && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">
                    Account Deletion Scheduled
                  </h3>
                  <p className="text-red-700 mb-4">
                    Your account will be permanently deleted on{' '}
                    <strong>
                      {new Date(user.deletion_scheduled_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </strong>
                  </p>
                  <Button
                    onClick={handleCancelDeletion}
                    style={{ backgroundColor: '#62D84E' }}
                  >
                    Cancel Deletion
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export Your Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Download a copy of all your data stored on Snow Exchange (GDPR compliant).
              This includes your profile, company information, and all projects/resources.
            </p>
            <Button
              onClick={handleExportData}
              variant="outline"
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export Data (JSON)'}
            </Button>
          </CardContent>
        </Card>

        {/* Account Deletion */}
        {!isDeletionScheduled && (
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                Delete Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-gray-600">
                  Permanently delete your Snow Exchange account and all associated data.
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">Before you delete:</h4>
                  <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                    <li>You have 7 days to cancel the deletion</li>
                    <li>After 7 days, all data will be permanently deleted</li>
                    <li>This action cannot be undone after the grace period</li>
                    <li>Consider exporting your data first</li>
                  </ul>
                </div>
                <Button
                  onClick={() => setShowDeleteModal(true)}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete My Account
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Confirm Account Deletion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                This will start a 7-day grace period. Your account will be permanently deleted after this period.
              </p>
              <div>
                <Label htmlFor="confirm-delete">
                  Type <strong>DELETE MY ACCOUNT</strong> to confirm:
                </Label>
                <Input
                  id="confirm-delete"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE MY ACCOUNT"
                  className="mt-2"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleRequestDeletion}
                  disabled={deleteConfirmText !== 'DELETE MY ACCOUNT' || isDeleting}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {isDeleting ? 'Processing...' : 'Confirm Deletion'}
                </Button>
                <Button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                  }}
                  variant="outline"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SecurityPage;
